import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { MigrationCtx } from '@/lib/migration/useMigration'
import { isApiError } from '@/api/http'
import { copiesOf, noteRequired, suggestedLabelSuffix, versionLabel } from '@/lib/migration/engine'
import { inScope } from '@/lib/migration/summary'
import { buildExportLog, buildPlanDocx, buildZip, downloadBlob, planFilenames } from '@/lib/migration/export'
import type { ExportFormat, MigrationPlanVersion, PlanCheck } from '@/api/types/migration'
import { RdModal, RdField } from '@/components/readiness/RdModal'
import { Chip, Icon } from '@/components/readiness/primitives'
import { formatDate } from '@/components/common/format'

// Review & export submission plan (I3 §11): centred 820px modal, same frame
// as the I2 §8 review & save. Saves an immutable version of Part A and every
// Part B annex in qtz-readiness, then exports it in CSA's layout. Blocking
// checks disable Export and are re-run by the backend on save (409
// BLOCKING_CHECKS); attention rows go to the log.

type Choice = 'docx' | 'pdf' | 'both'

export function ReviewExportModal({ ctx, onClose, onDone }: { ctx: MigrationCtx; onClose: () => void; onDone: (version: MigrationPlanVersion, files: string[]) => void }) {
  const { index, indexId, actor, plan: data, actions, summary } = ctx
  // The live draft: every copy saved or exported here belongs to v{n}.
  const n = data.plan?.plan_state.n ?? 1
  const copies = copiesOf(data.versions, n)
  const today = new Date()
  const [suffix, setSuffix] = useState(suggestedLabelSuffix(today))
  const label = versionLabel(n, suffix)
  const needsNote = noteRequired(n)
  const [date, setDate] = useState(today.toISOString().slice(0, 10))
  const [note, setNote] = useState('')
  const [choice, setChoice] = useState<Choice>('docx')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const noteRef = useRef<HTMLTextAreaElement>(null)
  // A revised plan cannot be saved or exported without its key changes: start there.
  useEffect(() => {
    if (needsNote) noteRef.current?.focus()
  }, [needsNote])
  const q = `?index=${indexId}`

  const changed = useMemo(() => {
    if (!data.plan) return { fields: 0, annexes: 0 }
    // Against the last copy of the previous version (the submitted one), else the last copy.
    const prev = data.versions.filter((v) => (v.plan_version ?? 1) < n)
    const since = (prev[prev.length - 1] ?? data.versions[data.versions.length - 1])?.created_at ?? '0'
    const annexes = inScope(data.annexes)
    const fields = (data.plan.updated_at > since ? 1 : 0) + annexes.filter((a) => a.updated_at > since && a.created_at <= since).length + annexes.flatMap((a) => a.blocks).filter((b) => b.updated_at > since).length
    return { fields, annexes: annexes.filter((a) => a.created_at > since).length }
  }, [data, n])
  // Section-2 blocks that came from Discovery, against every block in scope.
  const assetsCovered = useMemo(() => {
    const blocks = inScope(data.annexes).flatMap((a) => a.blocks)
    return { covered: blocks.filter((b) => !!b.source_id).length, total: blocks.length }
  }, [data.annexes])

  if (!data.plan || !summary) return null
  const checks = summary.checks
  const blocking = checks.blocking.length
  // Saving a copy is allowed while checks block; exporting is not.
  const noteMissing = needsNote && !note.trim()
  const canSave = !noteMissing && !busy
  const canExport = blocking === 0 && canSave
  const formats: ExportFormat[] = choice === 'both' ? ['docx', 'pdf'] : [choice]

  const fixLink = (c: PlanCheck) => {
    if (c.fix.item_id) return `/readiness/tracker${q}&item=${c.fix.item_id}`
    if (c.fix.part === 'a') return `/readiness/submission/plan${q}&part=a${c.fix.section ? `#${c.fix.section}` : ''}`
    if (c.fix.annex_id) return `/readiness/submission/plan${q}&annex=${c.fix.annex_id}${c.fix.asset_id ? `#block-${c.fix.asset_id}` : ''}`
    if (c.fix.system_id) return `/readiness/submission/plan${q}&add=${c.fix.system_id}`
    return `/readiness/submission/plan${q}`
  }

  const run = async (exportNow: boolean) => {
    setBusy(true)
    setError(null)
    try {
      const at = new Date()
      const version = await actions.saveVersion({ label: label.trim(), submission_date: date, note: note.trim() || null, export: exportNow ? formats : [] })
      const files: string[] = []
      if (exportNow) {
        const names = planFilenames(index, version)
        if (choice === 'docx') {
          downloadBlob(await buildPlanDocx(index, version, at), names.docx)
          files.push(names.docx)
        } else if (choice === 'pdf') {
          files.push(names.pdf)
        } else {
          const docx = await buildPlanDocx(index, version, at)
          downloadBlob(await buildZip([{ name: names.docx, blob: docx }, { name: names.log, blob: buildExportLog(index, version, at) }]), names.zip)
          files.push(names.zip, names.pdf)
        }
        if (formats.includes('pdf')) window.open(`/readiness/submission/versions/${version.id}/print${q}&print=1`, '_blank', 'noopener')
      }
      onDone(version, files)
    } catch (e) {
      const blocking = isApiError(e, 'BLOCKING_CHECKS') ? ((e.data as { blocking?: string[] } | undefined)?.blocking ?? []) : null
      setError(blocking ? `Export refused · blocking checks: ${blocking.join(', ')}` : e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (canExport) void run(true)
  }

  return (
    <RdModal
      title="Review & export submission plan"
      width={820}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rd-btn mr-auto" onClick={onClose}>Back</button>
          {noteMissing ? (
            <button type="button" className="inline-flex items-center gap-1 self-center text-[12.5px] underline-offset-2 hover:underline" style={{ color: 'var(--rd-status-warn)' }} onClick={() => noteRef.current?.focus()} data-testid="note-missing">
              <Icon name="warn" size={13} /> Add the key changes since v{n - 1} to save or export
            </button>
          ) : (
            <span className="self-center text-[12.5px]" style={{ color: blocking ? 'var(--rd-status-warn)' : 'var(--rd-text-muted)' }} data-testid="blocking-count">{blocking} blocking {blocking === 1 ? 'check' : 'checks'}</span>
          )}
          <button type="button" className="rd-btn" disabled={!canSave} onClick={() => void run(false)} data-testid="save-version-only" title={blocking ? 'Saves a copy of the plan as it stands; export stays blocked until the checks pass' : undefined}><Icon name="save" size={14} /> Save copy only</button>
          <button type="submit" form="rd-review-export" className="rd-btn rd-btn-primary" disabled={!canExport} data-testid="export-version"><Icon name="download" size={14} /> {busy ? 'Exporting…' : `Export v${n}`}</button>
        </>
      }
    >
      <p className="-mt-1 mb-4 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Saves a read-only copy of v{n} (Part A and every Part B annex), then exports it in CSA's layout · {index.name} {index.version}</p>
      <form id="rd-review-export" onSubmit={submit}>
        <div className="grid gap-3 sm:grid-cols-[1fr_170px]">
          <RdField label="Version" htmlFor="pv-label" hint={copies.length ? `${copies.length} ${copies.length === 1 ? 'copy' : 'copies'} of v${n} saved already; this one is also v${n}` : `The version moves to v${n + 1} only after v${n} is marked as submitted`}>
            <div className="rd-input flex items-center gap-2 !py-0 pr-0">
              <span className="rd-mono shrink-0 font-semibold" data-testid="version-number">v{n} ·</span>
              <input id="pv-label" className="min-w-0 flex-1 bg-transparent py-2 outline-none" value={suffix} onChange={(e) => setSuffix(e.target.value)} placeholder="e.g. Q4 2026 update" aria-label="Version label" data-testid="version-suffix" />
            </div>
          </RdField>
          <RdField label="Submission date" htmlFor="pv-date">
            <input id="pv-date" className="rd-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </RdField>
        </div>
        <RdField
          label={needsNote ? `Key changes since the previous submitted version (v${n - 1})` : 'Note (optional)'}
          htmlFor="pv-note"
          required={needsNote}
          hint={noteMissing
            ? <span style={{ color: 'var(--rd-status-warn)' }} data-testid="note-required-hint">Required: Save and Export stay disabled until this is filled. CSA asks a revised plan to state its key changes; they print under Version in the export.</span>
            : needsNote ? 'CSA asks a revised plan to state its key changes. Printed under Version in the export.' : 'Printed under Version in the export.'}
        >
          <textarea ref={noteRef} id="pv-note" className="rd-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={needsNote ? `What changed since v${n - 1}, e.g. updated budget allocation; added Card Vault annex` : 'What changed in this version'} aria-invalid={noteMissing || undefined} style={noteMissing ? { borderColor: 'var(--rd-status-warn)' } : undefined} data-testid="version-note" />
        </RdField>

        <div className="mt-1 grid grid-cols-2 gap-2 lg:grid-cols-4">
          <Tile label="Part A" value={`${summary.part_a.answered} / 6`} hint="sections answered" />
          <Tile label="CII systems covered" value={`${summary.cii_covered} / ${summary.cii_count}`} hint={summary.missing_systems.length ? `${summary.missing_systems.length} CII ${summary.missing_systems.length === 1 ? 'system' : 'systems'} without an annex` : `${summary.annex_count} ${summary.annex_count === 1 ? 'annex' : 'annexes'} in total`} warn={summary.missing_systems.length > 0} testId="tile-annexes" />
          <Tile label="Assets from Discovery" value={`${assetsCovered.covered} of ${assetsCovered.total}`} hint="section-2 rows filled from Discovery sources" />
          <Tile label={n > 1 ? `Changed since v${n - 1}` : 'Changed since last copy'} value={`${changed.fields}`} hint={`${changed.fields === 1 ? 'record' : 'records'} · ${changed.annexes} new ${changed.annexes === 1 ? 'annex' : 'annexes'}`} />
        </div>

        <section className="mt-4" aria-labelledby="pe-blocking">
          <p id="pe-blocking" className="rd-label">Blocking checks {blocking === 0 ? '(none today)' : `(${blocking})`}</p>
          <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>An unanswered Part A section, a CII system with no annex, an annex with zero assets or missing system details, or an asset missing its model, protocols, migration decision, package, hosting, dependencies or solution blocks the export. A copy can still be saved.</p>
          {blocking > 0 && (
            <ul className="mt-1.5 space-y-1" data-testid="plan-blocking-checks">
              {checks.blocking.map((c) => (
                <li key={c.code} className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-[12.5px]" style={{ background: 'var(--rd-bg-tile)', border: '1px solid var(--rd-status-warn)' }}>
                  <Icon name="warn" size={13} style={{ color: 'var(--rd-status-warn)' }} />
                  <span className="min-w-0 flex-1">{c.text}</span>
                  <Link to={fixLink(c)} className="rd-link" onClick={onClose}>Fix →</Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-4" aria-labelledby="pe-attention">
          <p id="pe-attention" className="rd-label">Needs attention ({checks.attention.length}) · written to the export log</p>
          {checks.attention.length === 0 ? (
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Nothing to flag.</p>
          ) : (
            <ul className="mt-1.5 space-y-1">
              {checks.attention.slice(0, 8).map((c) => (
                <li key={c.code} className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-1.5 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)' }}>
                  <Icon name="info" size={13} style={{ color: 'var(--rd-status-warn)' }} />
                  <span className="min-w-0 flex-1">{c.text}</span>
                  {(c.kind === 'annex_stale_discovery' || c.kind === 'overridden_source_changed') && <button type="button" className="rd-link" onClick={() => void ctx.migrationSync()}>Re-sync</button>}
                  <Link to={fixLink(c)} className="rd-link" onClick={onClose}>Open →</Link>
                </li>
              ))}
              {checks.attention.length > 8 && <li className="text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>and {checks.attention.length - 8} more in the log</li>}
            </ul>
          )}
        </section>

        <fieldset className="mt-4">
          <legend className="rd-label">Export format</legend>
          <div className="mt-1.5 space-y-1.5 text-[14px]">
            {([
              ['docx', 'Word (.docx)', "CSA template layout, one Part B per system"],
              ['pdf', 'PDF', 'print-ready, light theme'],
              ['both', 'Both', 'zipped with the export log'],
            ] as const).map(([k, l, hint]) => (
              <label key={k} className="flex items-center gap-2">
                <input type="radio" name="pe-format" checked={choice === k} onChange={() => setChoice(k)} />
                <span className="font-medium">{l}</span> <span style={{ color: 'var(--rd-text-faint)' }}>· {hint}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {error && <p className="mt-3 rounded-lg px-3 py-2 text-[12.5px]" role="alert" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' }} data-testid="export-error">{error}</p>}

        <p className="mt-4 flex items-start gap-2 rounded-lg px-3 py-2 text-[11.5px]" style={{ background: 'var(--rd-bg-tile)', color: 'var(--rd-text-muted)' }}>
          <Icon name="info" size={13} className="mt-0.5 shrink-0" />
          <span>Each saved copy is read-only, stamped “saved by {actor} · {formatDate(today.toISOString())}”. Export as often as you need: every copy stays v{n} until v{n} is marked as submitted. {summary.items.by_status.verified > 0 && <Chip tone="warn" className="ml-1">{summary.items.by_status.verified} verified, awaiting re-scan · exported as “in progress”</Chip>}</span>
        </p>
      </form>
    </RdModal>
  )
}

function Tile({ label, value, hint, warn, testId }: { label: string; value: string; hint: string; warn?: boolean; testId?: string }) {
  return (
    <div className="rd-tile p-3">
      <p className="rd-label">{label}</p>
      <p className="rd-mono mt-1 text-[18px] font-bold leading-tight" data-testid={testId}>{value}</p>
      <p className="mt-0.5 text-[11.5px]" style={{ color: warn ? 'var(--rd-status-warn)' : 'var(--rd-text-muted)' }}>{hint}</p>
    </div>
  )
}

// Version list (opened by `Versions` on the plan page, §10.2): the saved
// copies grouped under the version they belong to, newest version first.
export function VersionsModal({ ctx, onClose }: { ctx: MigrationCtx; onClose: () => void }) {
  const { indexId, plan: data } = ctx
  const q = `?index=${indexId}`
  const numbers = [...new Set(data.versions.map((v) => v.plan_version ?? 1))].sort((a, b) => b - a)
  const submitted = new Set(data.submissions.map((x) => x.version))
  return (
    <RdModal title="Plan versions" width={640} onClose={onClose} footer={<button type="button" className="rd-btn" onClick={onClose}>Close</button>}>
      {numbers.length === 0 ? (
        <p className="text-[14px]" style={{ color: 'var(--rd-text-muted)' }}>No copies saved yet. Review & export saves the first one.</p>
      ) : (
        <div className="space-y-4" data-testid="version-list">
          {numbers.map((n) => {
            const copies = [...copiesOf(data.versions, n)].reverse()
            return (
              <section key={n} data-testid={`version-group-${n}`}>
                <p className="mb-1.5 flex items-center gap-2 text-[14px] font-semibold">v{n} {submitted.has(n) ? <Chip tone="ok">SUBMITTED</Chip> : <Chip tone="info">DRAFT</Chip>}<span className="text-[12px] font-normal" style={{ color: 'var(--rd-text-muted)' }}>{copies.length} {copies.length === 1 ? 'copy' : 'copies'}</span></p>
                <ul className="space-y-2">
                  {copies.map((v) => (
                    <li key={v.id} className="rd-tile p-3 text-[12.5px]">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-[13.5px] font-medium">{v.label}</p>
                        <span className="rd-mono" style={{ color: 'var(--rd-text-muted)' }}>{v.completeness}% · {v.snapshot.annexes.length} {v.snapshot.annexes.length === 1 ? 'annex' : 'annexes'}{v.blocking_count ? ` · ${v.blocking_count} blocking` : ''}</span>
                      </div>
                      <p className="mt-0.5" style={{ color: 'var(--rd-text-muted)' }}>Submission date {formatDate(v.submission_date)} · saved by {v.created_by} · {formatDate(v.created_at)}{v.note ? ` · “${v.note}”` : ''}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-2">
                        {v.exported_formats.length ? v.exported_formats.map((f) => <Chip key={f} tone="ok">{f}</Chip>) : <Chip tone="neutral">saved only</Chip>}
                        <Link to={`/readiness/submission/versions/${v.id}/print${q}`} className="rd-link ml-auto">Open print view →</Link>
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      )}
      <p className="mt-3 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>Saved copies are immutable. Saving or exporting never moves the version number; marking a version as submitted does. The CSA Part A header's version, key changes and submission date are rendered from the copy, never from the live plan.</p>
    </RdModal>
  )
}
